import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useState, useRef, useEffect } from "react";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { useColors, useAlert } from "@inbody/core";
import { useAuth } from "@inbody/keycloak";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Chip, Box, Typography } from "@mui/material";
dayjs.extend(duration);
function SessionTimer({ compact = false }) {
  const colors = useColors();
  const { keycloak, logout } = useAuth();
  const { toastAlert, confirmAlert } = useAlert();
  const [remainingTime, setRemainingTime] = useState(null);
  const hasShownExpiredAlert = useRef(false);
  const hasShownWarningAlert = useRef(false);
  useEffect(() => {
    var _a;
    if (!((_a = keycloak == null ? void 0 : keycloak.tokenParsed) == null ? void 0 : _a.exp)) {
      setRemainingTime(null);
      return;
    }
    const updateTimer = () => {
      var _a2;
      const now = Math.floor(Date.now() / 1e3);
      const exp = (_a2 = keycloak.tokenParsed) == null ? void 0 : _a2.exp;
      if (!exp) {
        setRemainingTime(null);
        return;
      }
      const diff = exp - now;
      if (diff <= 0) {
        setRemainingTime({ hours: 0, minutes: 0, seconds: 0 });
        if (!hasShownExpiredAlert.current) {
          hasShownExpiredAlert.current = true;
          keycloak == null ? void 0 : keycloak.updateToken(-1).then(() => {
            toastAlert.success("세션이 자동으로 연장되었습니다.");
            hasShownExpiredAlert.current = false;
          }).catch((error) => {
            console.error("토큰 갱신 실패:", error);
            toastAlert.error(
              "세션이 만료되었습니다. 현재 페이지 작업을 저장하고 다시 로그인해 주세요."
            );
            logout();
          });
        }
        return;
      }
      const d = dayjs.duration(diff, "seconds");
      const hours = Math.floor(d.asHours());
      const minutes = d.minutes();
      const seconds = d.seconds();
      const totalMinutes2 = hours * 60 + minutes;
      setRemainingTime({
        hours,
        minutes,
        seconds
      });
      if (compact && totalMinutes2 === 5 && seconds === 0 && !hasShownWarningAlert.current) {
        hasShownWarningAlert.current = true;
        void confirmAlert({
          title: "세션 연장",
          text: "세션이 5분 후 만료됩니다. 세션을 연장할까요?",
          icon: "warning",
          confirmButtonText: "연장",
          cancelButtonText: "취소"
        }).then(({ isConfirmed }) => {
          if (!isConfirmed) return;
          keycloak == null ? void 0 : keycloak.updateToken(-1).then(() => {
            toastAlert.success("세션이 연장되었습니다.");
            hasShownWarningAlert.current = false;
          }).catch((error) => {
            console.error("토큰 갱신 실패:", error);
            void confirmAlert({
              title: "세션 연장 실패",
              text: "세션 연장에 실패했습니다. 다시 로그인해 주세요.",
              icon: "error",
              showCancelButton: false
            }).finally(() => {
              logout();
            });
          });
        });
      }
    };
    updateTimer();
    const interval = setInterval(updateTimer, 1e3);
    return () => clearInterval(interval);
  }, [compact, confirmAlert, keycloak, logout, toastAlert]);
  if (!remainingTime) {
    return null;
  }
  const totalMinutes = remainingTime.hours * 60 + remainingTime.minutes;
  const isUrgent = totalMinutes < 30 && totalMinutes >= 10;
  const isCritical = totalMinutes < 10;
  if (compact) {
    return /* @__PURE__ */ jsx(
      Chip,
      {
        icon: /* @__PURE__ */ jsx(AccessTimeIcon, {}),
        label: `세션 ${remainingTime.hours}:${String(remainingTime.minutes).padStart(2, "0")}:${String(remainingTime.seconds).padStart(2, "0")}`,
        size: "small",
        sx: {
          backgroundColor: isCritical ? colors.brand[50] : isUrgent ? colors.yellow[50] : colors.blue[50],
          color: isCritical ? colors.brand[700] : isUrgent ? colors.yellow[700] : colors.blue[700],
          fontWeight: 600,
          "& .MuiChip-icon": {
            color: "inherit"
          }
        }
      }
    );
  }
  return /* @__PURE__ */ jsxs(
    Box,
    {
      sx: {
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: 2,
        borderRadius: "0.75rem",
        backgroundColor: isCritical ? colors.brand[50] : isUrgent ? colors.yellow[50] : colors.blue[50],
        border: `1px solid ${isCritical ? colors.brand[200] : isUrgent ? colors.yellow[200] : colors.blue[200]}`
      },
      children: [
        /* @__PURE__ */ jsx(
          AccessTimeIcon,
          {
            sx: {
              fontSize: 28,
              color: isCritical ? colors.brand[600] : isUrgent ? colors.yellow[600] : colors.blue[600]
            }
          }
        ),
        /* @__PURE__ */ jsxs(Box, { children: [
          /* @__PURE__ */ jsx(
            Typography,
            {
              sx: {
                fontSize: "0.75rem",
                color: isCritical ? colors.brand[700] : isUrgent ? colors.yellow[700] : colors.blue[700],
                fontWeight: 500,
                mb: 0.5
              },
              children: "세션 남은 시간"
            }
          ),
          /* @__PURE__ */ jsx(
            Typography,
            {
              sx: {
                fontSize: "1.5rem",
                fontWeight: 700,
                color: isCritical ? colors.brand[700] : isUrgent ? colors.yellow[700] : colors.blue[700]
              },
              children: remainingTime.hours > 0 ? /* @__PURE__ */ jsxs(Fragment, { children: [
                remainingTime.hours,
                "시간 ",
                remainingTime.minutes,
                "분",
                " ",
                remainingTime.seconds,
                "초"
              ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
                remainingTime.minutes,
                "분 ",
                remainingTime.seconds,
                "초"
              ] })
            }
          ),
          /* @__PURE__ */ jsx(
            Typography,
            {
              sx: {
                fontSize: "0.75rem",
                color: colors.gray[600],
                mt: 0.5
              },
              children: isCritical ? "곧 자동 로그아웃됩니다." : isUrgent ? "세션이 곧 만료됩니다." : "수동 연장 없이 만료 시 자동 로그아웃됩니다."
            }
          )
        ] })
      ]
    }
  );
}
export {
  SessionTimer
};
